import { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Text, Buttons, ModalContext } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { isAuthenticatedSelector } from 'Store/modules/auth/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useFetchGetCustomers } from 'Containers/Plans/hooks';
import FlexLiteInfo from 'Containers/Result/components/FlexLiteInfo';
import FlowColLayout from 'Components/Layouts/FlowColLayout';
import CustomTransition from 'Components/CustomTransition';
import Reviews from 'Components/Reviews';
import SaveCardModal from 'Components/Modal/components/SaveCardModal';
import { routes } from 'Constants/routes';
import { FRONT_PLANS, plansNameMapping } from 'Constants/plans';
import { TAX_YEAR } from 'Config';
import { ResultTypes } from 'Types/common';
import * as Styled from './styles';
import { useSubscribeLiteFlexCheckout } from './hooks';

const FLEX_LITE_TYPE = 'trial';

const FlexLiteView = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.RESULT);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const paymentLaterProps = useSelector(routeStateSelector);
  const { mutate } = useSubscribeLiteFlexCheckout({
    data: paymentLaterProps,
  });
  const { showModal } = useContext(ModalContext);
  const { data: customer } = useFetchGetCustomers(isAuthenticated);

  useEffect(() => {
    if (
      !paymentLaterProps &&
      !paymentLaterProps.taxReturnType &&
      !paymentLaterProps.taxReturnResult
    ) {
      history.push(routes.RESULT);
    }
  }, [paymentLaterProps]);

  useEffect(() => {
    if (customer && plansNameMapping(customer?.activePlan?.name) !== FRONT_PLANS.BASIC) {
      history.push(routes.RESULT);
    }
  }, [customer]);

  const checkoutParams = {
    filing: 'true',
    plans: FRONT_PLANS.FLEX_LITE,
    saveCard: 'true',
    submitFlexLite: 'true',
    isExtendedFlexLite: !paymentLaterProps?.notIsToReturn ? 'true' : 'false',
    isExtendedAndIsToReturn: (paymentLaterProps?.taxReturnType === ResultTypes.toReturn).toString(),
  };

  const handleClick = (): void => {
    if (paymentLaterProps?.fromFedUser) {
      showModal({
        comp: (
          <SaveCardModal
            successUrl={routes.CHECKOUT_SUCCESS_STEP}
            cancelUrl={routes.RESULT}
            checkoutParams={checkoutParams}
          />
        ),
      });
    } else {
      mutate({
        taxYear: TAX_YEAR.toString(),
        type: FLEX_LITE_TYPE,
      });
    }
  };

  return (
    <FlowColLayout section={paymentLaterProps?.section} isFlow>
      <CustomTransition start>
        <Styled.Container
          direction="column"
          align="center"
          justify="center"
          gap="dimension_24"
          fullHeight
        >
          <Text type="paragraphs" color="dark" size="XL_XL_S">
            {t('Flex.lite.title')}
          </Text>
          <FlexLiteInfo />
          <Styled.ButtonsContainer direction="column" fullWidth gap="dimension_24">
            <Buttons.Classic
              testID="payLater"
              mode="live"
              size="m"
              onClick={() => handleClick()}
              label={t('Result.CTA.paylater')}
            />
            <Text type="paragraphs" size="XS_S_S" color="grey" align="center">
              {t('Flex.lite.conditions')}
            </Text>
            <Reviews />
          </Styled.ButtonsContainer>
        </Styled.Container>
      </CustomTransition>
    </FlowColLayout>
  );
};

export default FlexLiteView;
